import React, { useEffect, useState } from "react";
import Cookies from "js-cookie";
import { getApi } from "../../utils/api";
import PageTitle from "../../components/PageTitle";
import SortableTable from "../../components/SortableTable";
import NoRecordsAvailable from "../../components/NoRecordsAvailable";
import { toggleMenu } from "../../utils/helpers";
import {
  API_GET_EMAIL_DELETION_REQUESTS,
  API_UPDATE_EMAIL_DELETION_REQUEST,
} from "../../config/Endpoints";
import { ThreeDots } from "react-loader-spinner";
import Select from "react-select";
import axios from "axios";
import { toast } from "react-toastify";
import * as Yup from "yup";

const REASON_MAX_CHARS = 200;

const validationSchema = Yup.object().shape({
  reason: Yup.string()
    .trim()

    .max(REASON_MAX_CHARS, `Maximum ${REASON_MAX_CHARS} characters allowed.`),

  action: Yup.string().required("Action is required"),
});

const ApproveEmailDeletion = ({ title, description }) => {
  const [employeeList, setEmployeeList] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [reason, setReason] = useState("");
  const [action, setAction] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmittingLoading, setIsSubmittingLoading] = useState(false);
  const accessToken = Cookies.get("accessToken");
  const [reasonError, setReasonError] = useState("");

  const [selectedStatus, setSelectedStatus] = useState(null);

  const requestStatusOptions = [
    { value: 0, label: "Pending" },
    { value: 1, label: "Approved" },
    { value: 2, label: "Rejected" },
  ];

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    setIsLoading(true);
    try {
      const res = await getApi(API_GET_EMAIL_DELETION_REQUESTS, accessToken);
      if (res.status === 200 && Array.isArray(res.data.data)) {
        setEmployeeList(res.data.data);
        setFilteredItems(res.data.data);
      }
    } catch (error) {
      console.error("Error fetching requests:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);

    let filtered = employeeList;
    if (value) {
      filtered = filtered.filter((item) =>
        item.fullName?.toLowerCase().includes(value)
      );
    }
    if (selectedStatus !== null) {
      filtered = filtered.filter((item) => item.reqStatus === selectedStatus);
    }

    setFilteredItems(filtered);
  };

  const handleFilterChange = (statusValue) => {
    setSelectedStatus(statusValue);
    if (statusValue === null) {
      setFilteredItems(employeeList);
    } else {
      const filtered = employeeList.filter(
        (item) => item.reqStatus === statusValue
      );
      setFilteredItems(filtered);
    }
  };

  const handleReset = () => {
    setReason("");
    setAction("");
    setReasonError("");
  };

  const handleSubmit = async () => {
    setReasonError("");

    try {
      await validationSchema.validate(
        { reason, action },
        { abortEarly: false }
      );
    } catch (validationError) {
      validationError.inner.forEach((err) => {
        if (err.path === "reason") setReasonError(err.message);
      });
      return;
    }
    const reqStatus = action === "APPROVED" ? 1 : 2;
    const approverReason = encodeURIComponent(reason);
    const employeeId = selectedEmployee.employeeId;

    try {
      setIsSubmittingLoading(true);
      const response = await axios.put(
        `${API_UPDATE_EMAIL_DELETION_REQUEST}/${employeeId}?approverReason=${approverReason}&reqStatus=${reqStatus}`,
        {},
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );

      if (response?.data?.message) {
        toast.success(response.data.message || "Request updated successfully!");
        fetchRequests();
        handleReset();
        document.querySelector(".btn-close").click();
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          "Something went Wrong. Please try again."
      );
    } finally {
      setIsSubmittingLoading(false);
    }
  };

  const columns = [
    { key: "fullName", label: "Employee Name" },
    { key: "designation", label: "Designation" },
    { key: "email", label: "Email" },
    { key: "reqReason", label: "Action Required" },
    {
      key: "reqStatus",
      label: "Status",
      render: (item) =>
        item.reqStatus === 0
          ? "Pending"
          : item.reqStatus === 1
          ? "Approved"
          : "Rejected",
    },
    {
      key: "action",
      label: "Action",
      render: (item) =>
        item.reqStatus === 0 ? (
          <a
            href="#"
            data-bs-toggle="modal"
            data-bs-target="#approveEmailModal"
            onClick={() => setSelectedEmployee(item)}
          >
            View
          </a>
        ) : (
          <p className="text-muted mb-0">-</p>
        ),
    },
  ];

  return (
    <div className="wrapper-body">
      {/* Header */}
      <div className="page-heading">
        <div id="showMenuBtn" className="collapse-button" onClick={toggleMenu}>
          <span></span>
          <span></span>
          <span></span>
        </div>
        <PageTitle title={title} description={description} />
      </div>

      {/* Search */}
      <div className="filters">
        <div className="search">
          <input
            type="text"
            name="search"
            placeholder="Search By Employee Name"
            value={searchTerm}
            onChange={handleSearch}
          />
          <img src="/assets/images/search-ico.svg" width="14" alt="Search" />
        </div>

        {/* Filter by Status */}
        <div className="search">
          <Select
            className="custom-select col-md-12 col-sm-12"
            classNamePrefix="react-select"
            options={requestStatusOptions}
            value={
              requestStatusOptions.find(
                (status) => status.value === selectedStatus
              ) || null
            }
            onChange={(selectedOption) =>
              handleFilterChange(selectedOption ? selectedOption.value : null)
            }
            placeholder="Filter by Status"
            isClearable
          />
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="centered-loader mt-5">
          <ThreeDots height="100" width="100" color="blue" />
        </div>
      ) : filteredItems.length > 0 ? (
        <SortableTable columns={columns} data={filteredItems} />
      ) : (
        <NoRecordsAvailable />
      )}

      {/* Modal */}
      <div
        className="modal fade"
        id="approveEmailModal"
        data-bs-backdrop="static"
        data-bs-keyboard="false"
        tabIndex="-1"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Approve Email Deletion</h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={handleReset}
              ></button>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit();
              }}
            >
              <div className="modal-body">
                <div className="table-responsive">
                  <table className="resume custom">
                    <thead>
                      <tr>
                        <th>Employee</th>
                        <th>Designation</th>
                        <th>Email</th>
                        <th>Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>{selectedEmployee.fullName}</td>
                        <td>{selectedEmployee.designation}</td>
                        <td>{selectedEmployee.email}</td>
                        <td>{selectedEmployee.reqReason}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="row mt-3">
                  <div className="col-md-2"></div>
                  <div className="col-md-4">
                    <label className="has-asterisk">Action</label>
                  </div>
                  <div className="col-md-5">
                    <select
                      className="border w-100"
                      value={action}
                      onChange={(e) => setAction(e.target.value)}
                      required
                    >
                      <option value="" disabled>
                        Select Action
                      </option>
                      <option value="APPROVED">Approve</option>
                      <option value="REJECTED">Reject</option>
                    </select>
                  </div>
                  <div className="col-md-2"></div>
                </div>

                <div className="row mt-3">
                  <div className="col-md-2"></div>
                  <div className="col-md-4">
                    <label className="has-asterisk">Reason</label>
                  </div>
                  <div className="col-md-5">
                    <textarea
                      rows={3}
                      className="border w-100"
                      placeholder="Enter reason"
                      value={reason}
                      onChange={(e) => {
                        const value = e.target.value;

                        if (value.startsWith(" ")) return;
                        setReason(value);
                        setReasonError("");
                      }}
                      required
                    ></textarea>
                    {reasonError && (
                      <p className="text-danger mt-1">{reasonError}</p>
                    )}
                  </div>
                  <div className="col-md-2"></div>
                </div>
              </div>

              <div className="modal-footer justify-content-center gap-3">
                <button
                  type="submit"
                  className="theme-button"
                  disabled={isSubmittingLoading}
                >
                  {isSubmittingLoading ? (
                    <ThreeDots
                      height="20"
                      width="60"
                      radius="9"
                      color="white"
                      ariaLabel="three-dots-loading"
                      visible={true}
                    />
                  ) : (
                    "Submit"
                  )}
                </button>
                <button
                  type="button"
                  className="theme-button bg-grey"
                  data-bs-dismiss="modal"
                  onClick={handleReset}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApproveEmailDeletion;
